import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, FormArray, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { BackboneComponent } from '@components/backbone/backbone.component';
import { BreadcrumbComponent } from '@components/breadcrumb/breadcrumb.component';
import { environment } from '@environments/environment';
import { CatalogService } from '@services/catalog.service';
import { SatService } from '@services/sat.service';
import { ToastService } from '@utils/toast.service';
import { requiredFileType, requiredFileSize } from '@utils/validators';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { IconFieldModule } from 'primeng/iconfield';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputIconModule } from 'primeng/inputicon';
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-cambio-emision',
  imports: [
    CommonModule,
    BackboneComponent,
    BreadcrumbComponent,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    RouterModule,
    InputSwitchModule,
    DropdownModule,
    CalendarModule,
    SelectModule,
    IconFieldModule,
    InputIconModule,
    InputGroupAddonModule,
    InputGroup,
    TooltipModule,
  ],
  templateUrl: './cambio-emision.component.html',
  styleUrl: './cambio-emision.component.scss',
})
export class CambioEmisionPage {
  private formBuilder = inject(FormBuilder);
  private catalogService = inject(CatalogService);
  private satService = inject(SatService);
  private toastService = inject(ToastService);

  public apiUrl = environment.app.apiUrl;
  public maxSizesUpload = 3000000;
  public isUploading = false;

  public list: any = [];
  public rangeDates: Date[] | undefined;
  public searchValue: string | undefined;
  public states: any = [];
  public municipalities: any = [];

  public formUpload = this.formBuilder.group({
    files: this.formBuilder.array([]),
    state: [null],
    municipality: [null],
  });

  get state() {
    return this.formUpload.get('state');
  }
  set state(value: any) {
    this.state.setValue(value);
  }
  get municipality() {
    return this.formUpload.get('municipality');
  }
  set municipality(value: any) {
    this.municipality.setValue(value);
  }
  get files(): FormArray {
    return this.formUpload.controls['files'] as FormArray;
  }
  getFilesAsIndex = (index: number, get = 'file') => this.files.controls[index].get(get);

  async ngOnInit() {
    this.catalogService.getStates().then((response) => (this.states = response));
    await this.refresh();
  }

  onStateChange = async () => {
    this.municipalities = [];
    this.municipality = null;
    this.municipality?.clearValidators();
    this.municipality?.disable();

    if (this.state.value !== null) {
      await this.catalogService.getMunicipalities(this.state.value).then((response) => {
        this.municipalities = [];
        this.municipalities = response;
        this.municipality = null;
        this.municipality?.setValidators(Validators.required);
        this.municipality?.enable();
      });
    }
  };

  refresh = async () => {
    await this.satService.list().then((response) => (this.list = response));
  };

  onInputFileChanged = (event: any) => {
    if (event.target.files && event.target.files.length) {
      for (const element of event.target.files) {
        let reader = new FileReader();

        reader.onload = (e: any) => {
          this.files.push(
            this.formBuilder.group({
              file: [element, [requiredFileType(['pdf']), requiredFileSize(this.maxSizesUpload)]],
              url: e.target.result, // Base64 string for preview image,
              upload: false,
            })
          );
        };

        reader.readAsDataURL(element);
      }
    }

    event.target.value = null;
  };

  removeFile = (index: number) => this.files.removeAt(index);

  upload = async () => {
    this.isUploading = true;

    const myFormData = new FormData();

    myFormData.append(`state`, this.state.value !== null ? this.states.filter((state: any) => state.code === this.state.value)[0].label : null);
    myFormData.append(
      `municipality`,
      this.municipality.value !== null ? this.municipalities.filter((municipality: any) => municipality.stateMunicipalityId === this.municipality.value)[0].label.toUpperCase() : null
    );

    for (const [index, item] of this.files.value.entries()) {
      if (this.getFilesAsIndex(index)?.errors === null) {
        myFormData.append('files[]', item.file, item.file.name);
      }
    }

    this.satService
      .upload(myFormData)
      .then(() => {
        this.isUploading = false;
        this.files.clear();

        this.refresh();
      })
      .catch((err) => {
        this.isUploading = false;
        this.toastService.error(err.error.data.message || 'Error al subir los archivos.');
      });
  };
}
